import { useMemo } from 'react'
import { DateTime } from 'luxon'
import {
  type AccountData,
  type AccountListInput,
  type CustomFieldFilter,
  type CustomFieldId,
  type CustomFieldSearchResult,
  type CustomFieldType,
  type DuplicateAccountsPreference,
  type ElasticFilter,
  isDefined,
  isEmpty,
  isNotEmpty,
  type SearchableValue,
  searchIncludes,
  type SmallAddress,
  type StatusHistoryItem,
  toAccountStatusId,
  toAdvancedCustomFields,
  type Workflow,
} from '@terros/common'
import { type SelectItem } from '../types'
import { useAccountStatuses } from '../accountStatus'

export const stringifyAccountAddress = (location: SmallAddress | undefined): string => {
  if (isEmpty(location)) return 'Unknown address'
  return location.line1 + ', ' + location.locality + ', ' + location.countrySubd + ' ' + location.postal1
}

export const stringifyAccountAddressWithUnit = (location: SmallAddress | undefined): string => {
  if (isEmpty(location)) return 'Unknown address'
  if (isEmpty(location.unitNbr)) return stringifyAccountAddress(location)
  return `${location.line1} #${location.unitNbr}, ${location.locality}, ${location.countrySubd} ${location.postal1}`
}

export const getStatusHistoryKey = (item: StatusHistoryItem): string => {
  return `${item.statusId}-${item.statusChangedDate}-${item.sourceStatus ?? ''}`
}

export const stringifyStatusHistoryItem = (item: StatusHistoryItem): string => {
  return `${DateTime.fromMillis(item.statusChangedDate).toLocaleString(DateTime.DATETIME_MED)} - ${item.statusId}${item.sourceStatus ? ` (${item.sourceStatus})` : ''}`
}

export const DUPLICATE_ACCOUNTS_ITEMS: SelectItem<DuplicateAccountsPreference>[] = [
  { label: 'Allow - Allow users to create duplicate accounts.', value: 'Allow' },
  { label: 'Warn - Warn users when creating duplicate accounts and require a confirmation.', value: 'Warn' },
  { label: 'Deny - Prevent users from creating duplicate accounts.', value: 'Deny' },
  { label: 'Auto - Automatically merge duplicate accounts.', value: 'Auto' },
]

export const searchAccount =
  (searchQuery: string) =>
  (account: AccountData | undefined): boolean => {
    if (!account) return false
    const compareList: SearchableValue[] = [JSON.stringify(account), stringifyAccountAddress(account.location)]

    if (isDefined(account.resident)) {
      compareList.push(account.resident)
    }
    if (isDefined(account.owner)) {
      compareList.push(account.owner)
    }
    if (isDefined(account.closer)) {
      compareList.push(account.closer)
    }

    const searchKey = searchQuery.toLowerCase()
    return searchIncludes(compareList, searchKey)
  }

export const buildAccountSearchInput = ({
  filters,
  workflow,
}: {
  filters: ElasticFilter
  workflow?: Workflow
}): AccountListInput => {
  const merged = mergeCustomFieldFilters(toAdvancedCustomFields(filters.customFields), filters.advancedCustomFields)
  return {
    searchInput: {
      ...filters,
      coordinates: isNotEmpty(filters.coordinates) ? filters.coordinates : undefined,
      teamIds: isNotEmpty(filters.teamIds) ? filters.teamIds : undefined,
      userId: filters.userIds?.at(0),
      statusIds: filters.statusIds ?? [],
      stageIds: isEmpty(filters.workflowStageIds) ? workflow?.stages.map((s) => s.id) : filters.workflowStageIds,
      workflowId: workflow?.id,
      searchByCurrentStage: true,
      customFields: merged,
    },
  }
}

export const mergeCustomFieldFilters = (
  legacy: CustomFieldFilter | undefined,
  advanced: CustomFieldFilter | undefined
): CustomFieldFilter | undefined => {
  if (isEmpty(legacy) && isEmpty(advanced)) return undefined
  const base: CustomFieldFilter = { ...legacy }
  if (isEmpty(advanced)) return isEmpty(base) ? undefined : base

  for (const [key, advancedValue] of Object.entries(advanced) as [CustomFieldId, (typeof advanced)[CustomFieldId]][]) {
    const legacyValue = base[key]
    if (!legacyValue) {
      // Key only in advanced — take it directly
      base[key] = advancedValue
    } else if (advancedValue.type === 'multipleChoice' && legacyValue.type === 'multipleChoice') {
      // Both multipleChoice — union the values with type-aware dedup so e.g. 1 and '1' stay distinct
      const seen = new Set(legacyValue.values.map(toDedupKey))
      const union = [...legacyValue.values, ...advancedValue.values.filter((v) => !seen.has(toDedupKey(v)))]
      base[key] = { type: 'multipleChoice', values: union }
    } else {
      // Conflict between range and multipleChoice — prefer advanced (range is the richer/newer shape)
      base[key] = advancedValue
    }
  }

  return isEmpty(base) ? undefined : base
}

const toDedupKey = (value: CustomFieldType): string => {
  if (value === null) return 'null:null'
  return `${typeof value}:${String(value)}`
}

export const useDecoratedAccountListInput = (input: AccountListInput): AccountListInput => {
  const { data: accountStatuses } = useAccountStatuses({ archived: 'all' })
  const statusIds = useMemo(() => {
    return accountStatuses?.statuses.map(toAccountStatusId) ?? []
  }, [accountStatuses])
  if (isEmpty(input.searchInput?.statusIds)) {
    return {
      ...input,
      searchInput: { ...input.searchInput, statusIds },
    }
  }
  return input
}
